import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useClients } from "../../hooks/useClients";
import ClientTable from "../../components/ClientTable";
import AddClientForm from "../../components/AddClientForm";
import { usePageContext } from "../../context/PageContext";
import { fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";

const Clients = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation, // usado para excluir 1 (segue como estava)
    isLoading,
  } = useClients(currentPage, pageSize, search);

  // Mesmo padrão de Products: empresa principal vinda do CompanySelector/sessionStorage
  const selectedCompanyIds = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      const arr = JSON.parse(raw || "[]");
      return Array.isArray(arr)
        ? arr.filter((n) => Number.isFinite(Number(n))).map(Number)
        : [];
    } catch {
      return [];
    }
  }, []);

  const firstProjectId = useMemo(
    () => (selectedCompanyIds.length ? selectedCompanyIds[0] : null),
    [selectedCompanyIds]
  );

  useEffect(() => {
    if (error) {
      setErrorMessage("Erro ao carregar clientes. Por favor, tente novamente.");
    } else {
      setErrorMessage(null);
    }
  }, [error]);

  // Busca o detalhe completo do cliente antes de abrir o form
  const openClientForEdit = async (idOrRow) => {
    try {
      setIsProcessing(true);
      setErrorMessage(null);

      const id = typeof idOrRow === "object" ? idOrRow?.id : idOrRow;
      if (!id) {
        // novo cadastro
        setSelectedClient({});
        return;
      }

      const resp = await fetchData(`/clients/${id}`, "GET");
      const fullClient = (resp && (resp.data || resp)) || {};
      setSelectedClient(fullClient);
    } catch (e) {
      setErrorMessage(
        (e && e.response && e.response.data && e.response.data.message) ||
          "Não foi possível carregar os dados completos do cliente."
      );
      // fallback: abre com o que veio da tabela
      if (typeof idOrRow === "object") setSelectedClient(idOrRow);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditClient = (clientOrId) => {
    openClientForEdit(clientOrId);
  };

  const handleDeleteClient = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteClient = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: () => {
        setErrorMessage("Erro ao excluir o cliente. Por favor, tente novamente.");
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  // <<< NOVO: exclusão em massa >>>
  const handleDeleteSelected = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Caso não exista um endpoint de bulk no backend, removemos 1 a 1.
      // Se você tiver /clients/bulk-delete, troque por uma única chamada POST/DELETE.
      const results = await Promise.allSettled(
        ids.map((id) => fetchData(`/clients/${id}`, "DELETE"))
      );

      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        setErrorMessage(
          `Nem todos os registros foram excluídos: ${failures} falharam de ${ids.length}.`
        );
      }
      await refetch();
    } catch (e) {
      setErrorMessage(
        (e && e.response && e.response.data && e.response.data.message) ||
          "Erro ao excluir os clientes selecionados."
      );
    } finally {
      setIsProcessing(false);
    }
  };
  // <<< FIM NOVO >>>

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    refetch();
  };

  const handleCloseForm = () => {
    setSelectedClient(null);
    setErrorMessage(null);
  };

  /**
   * Iniciar Conversa (nova solicitação de RELACIONAMENTO) para um cliente
   */
  const handleStartConversation = async (client) => {
    if (!client?.id) return;
    try {
      setIsProcessing(true);
      setErrorMessage(null);

      let projectId = null;
      try {
        const raw = sessionStorage.getItem("selectedCompanies");
        const arr = JSON.parse(raw || "[]");
        if (Array.isArray(arr) && arr.length > 0) projectId = Number(arr[0]);
      } catch {
        /* ignore */
      }

      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "clients-page",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;

      if (!conv?.id) {
        throw new Error("A API não retornou o ID da solicitação criada.");
      }

      const width = window.screen.width;
      const height = window.screen.height;
      window.open(
        `/popup-call/${conv.id}`,
        "SolicitaçãoPopup",
        `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
      );
    } catch (e) {
      setErrorMessage(
        (e && e.response && e.response.data && e.response.data.message) ||
          e?.message ||
          "Não foi possível iniciar a conversa."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box className="tour-clients-root" sx={{ paddingTop: 1 }}>
      <Box
        className="tour-clients-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("clientsPage.title")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => openClientForEdit(null)}
          disabled={isProcessing}
        >
          {t("clientsPage.addClient")}
        </Button>
      </Box>

      {selectedClient ? (
        <AddClientForm
          client={selectedClient}
          defaultProjectId={firstProjectId}
          isFetching={isProcessing || isLoading}
          onClose={handleCloseForm}
          onSubmit={async (updatedClient) => {
            setIsProcessing(true);
            try {
              await updateMutation.mutateAsync(updatedClient);
              await refetch();
              handleCloseForm();
            } catch (err) {
              // Mantém mensagem no contexto da página
              setErrorMessage(
                err?.message ||
                  err?.data?.message ||
                  (err && err.response && err.response.data && err.response.data.message) ||
                  "Erro ao salvar o cliente."
              );
              // Propaga para o formulário poder mapear para campos específicos
              throw err;
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <Box className="tour-clients-table">
          <ClientTable
            clients={data?.clients || []}
            rowCount={data?.pagination?.total || 0}
            pageSize={pageSize}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            isLoading={isLoading || isProcessing}
            onEditClient={handleEditClient}
            onDeleteClient={handleDeleteClient}
            onStartConversation={handleStartConversation}
            onDeleteSelected={handleDeleteSelected}
          />
        </Box>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este cliente?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteClient}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
